import React from 'react';
import { Route, Switch } from 'react-router-dom';
import loadable from '@loadable/component';
import { FourOhFourError } from '@sm/webassets';
import PageLoading from './components/PageLoading';

const Account = loadable(() => import(/* webpackChunkName: 'coreweb-account' */ './pages/account'), {
  fallback: <PageLoading />,
});

const Analysis = loadable(() => import(/* webpackChunkName: 'coreweb-analysis-base' */ './pages/analysis'), {
  fallback: <PageLoading />,
});

const AnalyzeSandbox = loadable(
  () => import(/* webpackChunkName: 'coreweb-analyze-sandbox' */ './pages/AnalyzeSandbox'),
  {
    fallback: <PageLoading />,
  }
);

const PublishedDashboard = loadable(
  () => import(/* webpackChunkName: 'coreweb-dashboards' */ './pages/PublishedDashboard'),
  {
    fallback: <PageLoading />,
  }
);

const InstantResults = loadable(
  () => import(/* webpackChunkName: 'coreweb-instant-results' */ './pages/InstantResults'),
  {
    fallback: <PageLoading />,
  }
);

const LiveResults = loadable(() => import(/* webpackChunkName: 'coreweb-live-results' */ './pages/LiveResults'), {
  fallback: <PageLoading />,
});

const CrosstabReports = loadable(
  () => import(/* webpackChunkName: 'coreweb-crosstab-reports' */ './pages/CrosstabReports'),
  {
    fallback: <PageLoading />,
  }
);

const AudienceRouter = loadable(() => import(/* webpackChunkName: 'coreweb-audience' */ './pages/audience'), {
  fallback: <PageLoading />,
});

const WebCampaignCollector = loadable(
  () => import(/* webpackChunkName: 'web-campaign-collector' */ './pages/WebCampaignCollector'),
  {
    fallback: <PageLoading />,
  }
);

const Profile = loadable(() => import(/* webpackChunkName: 'coreweb-profile' */ './pages/profile'), {
  fallback: <PageLoading />,
});

const ProfilePortable = loadable(
  () => import(/* webpackChunkName: 'coreweb-profile-portable' */ './pages/ProfilePortable'),
  {
    fallback: <PageLoading />,
  }
);

const Create = loadable(() => import(/* webpackChunkName: 'coreweb-create' */ './pages/CreateSurvey/CreateRouter'), {
  fallback: <PageLoading />,
});

const Solutions = loadable(() => import(/* webpackChunkName: 'coreweb-solutions' */ './pages/solutions'), {
  fallback: <PageLoading />,
});

const SharedTrackingSolution = loadable(
  () => import(/* webpackChunkName: 'coreweb-solutions' */ './pages/solutions/Tracking/Shared'),
  {
    fallback: <PageLoading />,
  }
);

const SolutionsFrameworkHeader = loadable(
  () => import(/* webpackChunkName: 'coreweb-solutions' */ './components/SolutionsFrameworkHeader'),
  {
    fallback: <PageLoading />,
  }
);

const SolutionsFrameworkCheckoutPanel = loadable(
  () => import(/* webpackChunkName: 'coreweb-solutions' */ './components/SolutionsFrameworkCheckoutPanel'),
  {
    fallback: <PageLoading />,
  }
);

const Team = loadable(
  () =>
    import(
      /* webpackChunkName: 'coreweb-team' */
      './pages/Team'
    ),
  {
    fallback: <PageLoading />,
  }
);

const User = loadable(() => import(/* webpackChunkName: 'coreweb-user' */ './pages/User'), {
  fallback: <PageLoading />,
});

const Billing = loadable(() => import(/* webpackChunkName: 'coreweb-billing' */ './pages/Billing'), {
  fallback: <PageLoading />,
});

const SurveysCore = loadable(() => import(/* webpackChunkName: 'coreweb-surveys-core' */ './pages/SurveysCore'), {
  fallback: <PageLoading />,
});

const SeatRequest = loadable(
  () => import(/* webpackChunkName: 'coreweb-seat-request' */ './pages/Team/ManageUsers/SeatRequest'),
  {
    fallback: <PageLoading />,
  }
);

const MoreSeatsRequest = loadable(
  () => import(/* webpackChunkName: 'coreweb-more-seats-request' */ './pages/Team/ManageUsers/MoreSeatsRequest'),
  {
    fallback: <PageLoading />,
  }
);

const CoreCheckout = loadable(() => import(/* webpackChunkName: 'coreweb-checkout' */ './pages/Checkout'), {
  fallback: <PageLoading />,
});

const Home = loadable(() => import(/* webpackChunkName: 'coreweb-home' */ './pages/Home'), {
  fallback: <PageLoading />,
});

const BuyResponses = loadable(() => import(/* webpackChunkName: 'coreweb-responses' */ './pages/Responses'), {
  fallback: <PageLoading />,
});

const Ml = loadable(() => import(/* webpackChunkName: 'coreweb-responses' */ './pages/ml'), {
  fallback: <PageLoading />,
});

const CollectorList = loadable(() => import(/* webpackChunkName: 'coreweb-collect-list' */ './pages/CollectorList'), {
  fallback: <PageLoading />,
});

const EmailCollectorOptions = loadable(
  () => import(/* webpackChunkName: 'coreweb-email-collector-options' */ './pages/EmailCollectorOptions'),
  {
    fallback: <PageLoading />,
  }
);

const Paywall2PlanModal = loadable(
  () => import(/* webpackChunkName: 'coreweb-paywall-modal' */ './pages/Paywall2PlanModal'),
  {
    fallback: <PageLoading />,
  }
);

const RequestAccess = loadable(() => import(/* webpackChunkName: 'coreweb-request-access' */ './pages/RequestAccess'), {
  fallback: <PageLoading />,
});

const SharePreview = loadable(() => import(/* webpackChunkName: 'coreweb-share-preview' */ './pages/SharePreview'), {
  fallback: <PageLoading />,
});

const EmbedsModule = loadable(() => import(/* webpackChunkName: 'coreweb-embed' */ './pages/Embeds/EmbedRouter'), {
  fallback: <PageLoading />,
});

const App = () => {
  return (
    <Switch>
      <Route path="/:page(summary|stories|connect)/:encryptedSid">
        <SurveysCore />
      </Route>
      <Route path="/analyze" component={Analysis} />
      <Route path="/analyze-sandbox" component={AnalyzeSandbox} />
      <Route path="/dashboards/:sharedKey" component={PublishedDashboard} />
      <Route path="/live/results" component={LiveResults} />
      <Route path="/results/:sharedViewKey/instant" component={InstantResults} />
      <Route path="/crosstabs" component={CrosstabReports} />
      <Route path="/collect/audience" component={AudienceRouter} />
      <Route path="/collect/list-new" component={CollectorList} />
      <Route path="/collect/emailoptions" component={EmailCollectorOptions} />
      <Route path="/:page(collect)/wc/:collectorId" component={WebCampaignCollector} />
      <Route path="/profile/paywall-2-plan-modal" component={Paywall2PlanModal} />
      <Route path="/profile" exact component={Profile} />
      <Route path="/profile/portable" component={ProfilePortable} />
      <Route path="/profile/:profilePath" component={Profile} />
      <Route path="/create" component={Create} />
      <Route path="/solutions/tracking/shared/:slug" component={SharedTrackingSolution} />
      {/* TODO: Remove the following 2 non-"setup" routes as part of MOD-10083 */}
      <Route
        path="/solutions/:solutionCategory/:solutionId/studies/:studyId/study-header"
        component={SolutionsFrameworkHeader}
      />
      <Route
        path="/solutions/:solutionCategory/:solutionId/studies/:studyId/checkout-panel"
        component={SolutionsFrameworkCheckoutPanel}
      />
      <Route
        path="/solutions/:solutionCategory/:solutionId/studies/:studyId/setup/study-header"
        component={SolutionsFrameworkHeader}
      />
      <Route
        path="/solutions/:solutionCategory/:solutionId/studies/:studyId/setup/checkout-panel"
        component={SolutionsFrameworkCheckoutPanel}
      />
      <Route path="/market-research/solutions/get-started" component={Solutions} />
      <Route path="/solutions" component={Solutions} />
      <Route path="/team/users/seat_request" component={SeatRequest} />
      <Route path="/team/users/more_seats_request" component={MoreSeatsRequest} />
      <Route path="/team" component={Team} />
      <Route path="/user/account/select" component={Account} />
      <Route path="/user" component={User} />
      <Route path="/:page(collect)/add">
        <SurveysCore />
      </Route>
      <Route path="/billing" component={Billing} />
      <Route path="/checkout" component={CoreCheckout} />
      <Route path="/home" component={Home} />
      <Route path="/buy-responses" component={BuyResponses} />
      <Route path="/ml" component={Ml} />
      <Route path="/request_access/:requestedId" component={RequestAccess} />
      <Route path="/share-preview/:mangledId" component={SharePreview} />
      <Route path="/embed/:moduleName/:featureKey?" component={EmbedsModule} />
      {/* Catch all route */}
      <Route component={FourOhFourError} />
    </Switch>
  );
};

export default App;
